import { Tab } from '@mui/material';
import { Fragment, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { generatePath, useNavigate } from 'react-router-dom';

import { PageHeader, QueryTabPanel, QueryTabs } from 'components';
import { NEW_ID } from 'constants/common.constants';
import { ROUTE_PATH } from 'constants/routes';
import { DynamicAdapterConfigsPage } from 'features/dynamic-adapter-configs';
import { ProviderTypesPage } from 'features/provider-types';
import { useMeta, useQueryTab, useUser } from 'hooks';
import { TranslationNamespace } from 'i18n';

import { P2PProvidersPage } from './P2PProvidersPage';

enum P2PProvidersTab {
  Providers = 'providers',
  DynamicAdapters = 'dynamic-adapters',
  ProviderTypes = 'provider-types',
}

export const P2PProvidersTabs: React.FC = () => {
  const { t } = useTranslation(TranslationNamespace.Admin, {
    keyPrefix: 'pages.p2p_providers',
  });
  const { isDynamicAdaptersEnabled } = useMeta();
  const { isAdmin, isTechOperator } = useUser();
  const navigate = useNavigate();
  const { tab } = useQueryTab<P2PProvidersTab>();

  const dynamicAdapterCreateRef = useRef<(() => void) | null>(null);
  const providerTypeCreateRef = useRef<(() => void) | null>(null);

  const canManage = isAdmin || isTechOperator;

  const handleCreate = useCallback(() => {
    if (tab === P2PProvidersTab.Providers) {
      const detailsPath = isTechOperator
        ? ROUTE_PATH.TECH_OPERATOR.P2P_PROVIDERS_DETAILS
        : isAdmin
        ? ROUTE_PATH.ADMIN.P2P_PROVIDERS_DETAILS
        : undefined;
      if (detailsPath) {
        navigate(generatePath(detailsPath, { id: NEW_ID }));
      }
    } else if (tab === P2PProvidersTab.DynamicAdapters) {
      dynamicAdapterCreateRef.current?.();
    } else if (tab === P2PProvidersTab.ProviderTypes) {
      providerTypeCreateRef.current?.();
    }
  }, [tab, isTechOperator, isAdmin, navigate]);

  if (!isDynamicAdaptersEnabled) {
    return <P2PProvidersPage />;
  }

  return (
    <Fragment>
      <PageHeader
        title={t('title')}
        divider={false}
        rightContentButton={canManage ? { onClick: handleCreate } : false}
      />
      <QueryTabs tabsEnum={P2PProvidersTab}>
        <Tab value={P2PProvidersTab.Providers} label={t('tabs.providers')} />
        <Tab
          value={P2PProvidersTab.DynamicAdapters}
          label={t('tabs.dynamic_adapters')}
        />
        <Tab
          value={P2PProvidersTab.ProviderTypes}
          label={t('tabs.provider_types')}
        />
      </QueryTabs>
      <QueryTabPanel value={P2PProvidersTab.Providers}>
        <P2PProvidersPage />
      </QueryTabPanel>
      <QueryTabPanel value={P2PProvidersTab.DynamicAdapters}>
        <DynamicAdapterConfigsPage onCreateRef={dynamicAdapterCreateRef} />
      </QueryTabPanel>
      <QueryTabPanel value={P2PProvidersTab.ProviderTypes}>
        <ProviderTypesPage onCreateRef={providerTypeCreateRef} />
      </QueryTabPanel>
    </Fragment>
  );
};
